import {
  useExcludedConsumersList,
  useExcludedConsumersSummary,
  useRunDetail,
} from "@features/bx/BulkBillGeneration/hooks";
import { GenericTable } from "@shared/components/GenericTable";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { PAGE_SIZE } from "@shared/utils/constants";
import { AlertCircle } from "lucide-react";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { GenerateBillsModal } from "./GenerateBillModal";

interface MetricProps {
  label: string;
  value: React.ReactNode;
  sub?: string;
  valueClass?: string;
}

const Metric = ({ label, value, sub, valueClass }: MetricProps) => (
  <div>
    <p className="text-xs font-medium text-gray-600 uppercase mb-1">{label}</p>
    <p className={`text-2xl font-bold ${valueClass ?? "text-gray-900"}`}>
      {value}
    </p>
    {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
  </div>
);

export const ExcludedConsumersTab = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const { id } = useParams<{ id: string }>();
  const [openModal, setOpenModal] = useState(false);
  const { data: runDetails } = useRunDetail(id);
  const { data: excludedData } = useExcludedConsumersList(id);
  const { data: excludedConsumerSummary } = useExcludedConsumersSummary(id);
  const currency = useCurrency();

  const metricsConfig: MetricProps[] = [
    {
      label: "Unbilled Consumers",
      value: excludedConsumerSummary?.unbilledConsumers,
      sub: `${excludedConsumerSummary?.unbilledPercentage}% of total`,
    },
    {
      label: "Estimated Revenue Loss",
      value: `${currency} ${excludedConsumerSummary?.estimatedRevenueLoss}`,
      valueClass: "text-red-600",
      sub: "Based on avg bill amount",
    },
    {
      label: "Potential Recovery",
      value: `${currency} ${excludedConsumerSummary?.potentialRecovery}`,
      valueClass: "text-green-600",
      sub: `${excludedConsumerSummary?.recoveryRate}% recovery rate`,
    },
    {
      label: "Action Status",
      value: excludedConsumerSummary?.actionStatus,
      valueClass: "text-blue-600",
      sub: "Awaiting bill generation",
    },
  ];

  const recoveryRecommendations = [
    `Generate bills for excluded consumers to recover estimated revenue of ${currency} ${excludedConsumerSummary?.estimatedRevenueLoss}`,
    "Review exclusion reasons and resolve issues to enable billing",
    "Prioritize high-value consumers for immediate action",
  ];

  const columns: TableColumn<any>[] = [
    { key: "accountNo", header: "Account No", type: "text" },
    { key: "name", header: "Name", type: "text" },
    { key: "plan", header: "Plan", type: "text" },
    { key: "serviceType", header: "Service", type: "text" },
    {
      key: "lastBillAmount",
      header: "Last Bill Amount",
      type: "custom",
      render: (value: number) => `${currency} ${value}`,
    },
    { key: "lastBillDate", header: "Last Bill Date", type: "text" },
    {
      key: "lastPaymentAmount",
      header: "Last Payment",
      type: "custom",
      render: (value: number) => `${currency} ${value ?? "NA"}`,
    },
    { key: "lastPaymentDate", header: "Last Payment Date", type: "text" },
  ];

  const totalItems = excludedData?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);

  return (
    <div className="p-6">
      {/* Revenue Leakage Alert */}
      <div className="mb-6 bg-yellow-50 border-2 border-yellow-200 rounded-lg p-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-full">
              <AlertCircle className="h-5 w-5 text-yellow-700" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                Revenue Leakage Alert
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Potential revenue loss from excluded consumers
              </p>
            </div>
          </div>

          <Badge className="bg-orange-100 text-orange-800">
            Action Required
          </Badge>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-4 gap-6 mb-6">
          {metricsConfig.map((metric) => (
            <Metric key={metric.label} {...metric} />
          ))}
        </div>

        {/* Recovery Recommendations */}
        <div className="border border-yellow-200 bg-white rounded-lg p-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">
            Recovery Recommendations
          </h4>

          <ul className="list-disc pl-5 space-y-2 text-sm text-gray-700">
            {recoveryRecommendations.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border p-6">
        {/* Table Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Excluded Consumers
            </h3>
            <p className="text-sm text-gray-500">
              Consumers excluded from the current billing cycle
            </p>
          </div>

          <Button onClick={() => setOpenModal(true)}>Generate Bills</Button>
        </div>

        <GenericTable
          data={excludedData?.result || []}
          columns={columns}
          rowKey="accountNo"
          emptyMessage="No excluded consumers"
          pagination={{
            currentPage,
            totalPages,
            totalItems,
            itemsPerPage: PAGE_SIZE,
            startItem: (currentPage - 1) * PAGE_SIZE + 1,
            endItem: Math.min(currentPage * PAGE_SIZE, totalItems),
          }}
          onPageChange={setCurrentPage}
        />
      </div>

      <GenerateBillsModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        startDate={runDetails?.cycleDetails?.billingPeriodStart}
        endDate={runDetails?.cycleDetails?.billingPeriodEnd}
        selectedCount={excludedData?.count}
      />
    </div>
  );
};
